/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * SessionEpics handles async session actions
 */
import { Injectable } from '@angular/core';
import {
  rx,
  Observable,
  observableOf,
  observableEMPTY,
  observableForkJoin,
  observableTimer,
  observableRace,
} from '@shared/rx';
import { Router } from '@angular/router';
import { find, get, isEmpty, isEqual, map } from 'lodash';
import { combineEpics, Epic, StateObservable } from 'redux-observable';
import { ACCESS_TOKEN, PERSONAL_TOKEN } from '@config/global-constants';
import {
  XprtDataService,
  XprtSessionService,
  PermissionService,
  TokenSessionService,
  TranslationService,
} from '@shared/services';
import { IAction, IEpicsService } from '@shared/types';
import { XprtDataActions, SessionActions, ToolsActions } from '../../actions';
import { DASHBOARD_ROUTE, LOGIN_ROUTE } from '@app/routes';
import { IAppState } from '@app/store';
import { PreferencesActions } from '@app/store/actions/preferences/preferences.actions';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SessionEpics implements IEpicsService {
  // eslint-disable-next-line max-params
  public constructor(
    private toolsActions: ToolsActions,
    private tokenSessionService: TokenSessionService,
    private xprtSessionService: XprtSessionService,
    private xprtDataService: XprtDataService,
    private router: Router,
    private translateService: TranslateService,
    private translationService: TranslationService,
  ) {}

  /**
   * Return combined session epics
   *
   * @return
   */
  public getEpic(): Epic<any, any> {
    return combineEpics(
      this.restoreTokens.bind(this),
      this.restoreAccessToken.bind(this),
      this.restorePersonalToken.bind(this),
      this.fetchUserInfo.bind(this),
      this.fetchPersonalToken.bind(this),
      this.applicationStarted.bind(this),
      this.removeToken.bind(this),
      this.fetchAccessToken.bind(this),
      this.initialCompanySelection.bind(this),
      this.companyChanged.bind(this),
      this.fetchCompaniesBeforeSelect.bind(this),
      this.fetchTranslations.bind(this),
    );
  }

  // Retrive personal token on login button click
  private fetchPersonalToken(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.FETCH_PERSONAL_TOKEN].includes(action.type),
      ),
      rx.mergeMap(({ payload }) => {
        return this.xprtSessionService.fetchPersonalToken(payload).pipe(
          rx.map((servicePayload) => {
            this.tokenSessionService.saveToken(servicePayload, PERSONAL_TOKEN);

            return SessionActions.fetchPersonalTokenSuccessAction(
              servicePayload.token,
            );
          }),
          rx.catchError((errors) =>
            observableOf(SessionActions.fetchPersonalTokenErrorAction(errors)),
          ),
        );
      }),
    );
  }

  private fetchAccessToken(action$: Observable<IAction>): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.FETCH_ACCESS_TOKEN].includes(action.type),
      ),
      rx.mergeMap(({ payload }) => {
        return this.xprtSessionService.fetchAccessToken(payload.companyId).pipe(
          rx.map((servicePayload) => {
            this.tokenSessionService.saveToken(servicePayload, ACCESS_TOKEN);

            return SessionActions.fetchAccessTokenSuccessAction(
              servicePayload.token,
            );
          }),
          rx.catchError((errors) =>
            observableOf(SessionActions.fetchAccessTokenErrorAction(errors)),
          ),
        );
      }),
    );
  }

  // Retrive personal token on login button click
  private initialCompanySelection(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.FETCH_PERSONAL_TOKEN_SUCCESS].includes(action.type),
      ),

      rx.map(({ payload }) => {
        this.router.navigate([DASHBOARD_ROUTE]);

        return XprtDataActions.showCompanySelectorAction();
      }),
    );
  }

  private fetchCompaniesBeforeSelect(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [XprtDataActions.SHOW_COMPANY_SELECTOR].includes(action.type),
      ),
      rx.map(({ payload }) => XprtDataActions.fetchCompaniesAction()),
    );
  }

  // Retrive personal token on login button click
  private companyChanged(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [
          SessionActions.FETCH_ACCESS_TOKEN_SUCCESS,
          SessionActions.RESTORE_TOKENS_SUCCESS,
        ].includes(action.type),
      ),
      rx.mergeMap(() => {
        const state = store.value;
        const companyId = get(state, 'userSession.currentCompanyId', '');
        const companies = get(state, 'userSession.companies', []);
        const companyIsNotFetched = !find(companies, { id: companyId });
        const actions = [
          XprtDataActions.hideCompanySelectorAction(),
          PreferencesActions.fetchPreferencesForCompanyAction(),

          // fetch all required permissions
          SessionActions.fetchPermissionsAction(
            PermissionService.config.requiredAppCodes,
          ),
          SessionActions.fetchUserInfoAction(companyId),
        ];

        if (companyIsNotFetched) {
          actions.push(XprtDataActions.fetchCompaniesAction([companyId]));
        }

        return observableOf(...actions);
      }),
    );
  }

  /**
   * Trying to get token when `RESTORE_ACCESS_TOKEN` action was dispatched
   * If token was found it dispatches `TOKEN_RETRIEVED` action with token data in a payload
   *
   * @param action$ - Observable for actions
   */
  private restoreAccessToken(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.RESTORE_ACCESS_TOKEN].includes(action.type),
      ),
      rx.map(() => this.tokenSessionService.getToken(ACCESS_TOKEN)),
      rx.filter((token) => token !== null),
      rx.mergeMap((tokenPayload) =>
        observableOf(
          SessionActions.fetchAccessTokenSuccessAction(tokenPayload),
        ),
      ),
    );
  }

  private restorePersonalToken(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.RESTORE_PERSONAL_TOKEN].includes(action.type),
      ),
      rx.map(() => this.tokenSessionService.getToken(PERSONAL_TOKEN)),
      rx.filter((token) => token !== null),
      rx.mergeMap((token) =>
        observableOf(SessionActions.restorePersonalTokenSuccessAction(token)),
      ),
    );
  }

  private restoreTokens(action$: Observable<IAction>): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.RESTORE_TOKENS].includes(action.type),
      ),
      rx.map(() => ({
        accessToken: this.tokenSessionService.getToken(ACCESS_TOKEN),
        personalToken: this.tokenSessionService.getToken(PERSONAL_TOKEN),
      })),
      rx.filter(
        (tokensObj) => !!tokensObj.accessToken && !!tokensObj.personalToken,
      ),
      rx.mergeMap((tokensObj) =>
        observableOf(SessionActions.restoreTokensSuccessAction(tokensObj)),
      ),
    );
  }

  // fetch user info on when access token is retrieved
  private fetchUserInfo(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.FETCH_USER_INFO].includes(action.type),
      ),
      rx.mergeMap((action: IAction) => {
        return this.xprtDataService.getCurrentUserInfo().pipe(
          rx.map((user) => {
            return SessionActions.fetchUserInfoSuccessAction(user);
          }),
          rx.catchError((errors) => {
            const state: IAppState = store.value;
            const isIframeMode = get(state, ['userSession', 'isIframeMode']);
            const actions: IAction[] = [
              SessionActions.fetchUserInfoErrorAction(errors),
            ];

            if (!isIframeMode) {
              actions.push(SessionActions.logoutUserAction());
            }

            return observableOf(...actions);
          }),
        );
      }),
    );
  }

  /**
   * Remove token from session when `LOGOUT_USER` action was dispatched
   * It dispatches `TOKEN_REMOVED` action
   *
   * @param action$ - Observable for actions
   */
  private removeToken(action$: Observable<IAction>): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [SessionActions.LOGOUT_USER].includes(action.type)),
      rx.mergeMap(() => {
        this.tokenSessionService.removeToken(ACCESS_TOKEN);
        this.tokenSessionService.removeToken(PERSONAL_TOKEN);
        this.router.navigate([LOGIN_ROUTE]);

        return observableOf(SessionActions.tokenRemovedAction());
      }),
    );
  }

  private fetchTranslations(action$: Observable<IAction>): Observable<IAction> {
    const timeout$ = observableTimer(5000);

    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.FETCH_TRANSLATIONS].includes(action.type),
      ),

      rx.mergeMap(() => {
        const loadTranslation = (locale) => {
          return this.translateService['retrieveTranslations'](locale).pipe(
            rx.catchError(() => observableOf(true)),
          );
        };

        const { userLocale } = this.translationService.getLocals();

        const getTranslations$ = observableForkJoin(
          map([userLocale], loadTranslation),
        ).pipe(
          rx.switchMap(() => {
            if (
              !isEmpty(userLocale) &&
              !isEqual(userLocale, this.translateService.getDefaultLang()) &&
              !isEqual(userLocale, this.translateService.currentLang)
            ) {
              return this.translateService.use(userLocale);
            }

            return observableOf(true);
          }),
        );

        // get translation with timeout to avoid blocking app startup
        return observableRace(getTranslations$, timeout$).pipe(
          rx.switchMap(() =>
            observableOf(SessionActions.setTranslationInitializedAction(true)),
          ),
          rx.catchError(() =>
            observableOf(SessionActions.setTranslationInitializedAction(true)),
          ),
        );
      }),
    );
  }

  private applicationStarted(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    let isPreferencesFetched = false;

    return action$.pipe(
      rx.filter((action) =>
        [
          SessionActions.FETCH_PERMISSIONS_SUCCESS,
          SessionActions.FETCH_USER_INFO_SUCCESS,
          XprtDataActions.FETCH_COMPANIES_SUCCESS,
          PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY_SUCCESS,
          PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY_ERROR,
          SessionActions.SET_TRANSLATION_INITIALIZED,
        ].includes(action.type),
      ),

      rx.mergeMap((action: IAction) => {
        if (
          [
            PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY_SUCCESS,
            PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY_ERROR,
          ].includes(action.type)
        ) {
          isPreferencesFetched = true;
        }

        const state: IAppState = store.value;
        const isPermissionsFetched =
          get(state, ['userSession', 'userPermissions', 'total'], 0) > 0;
        const isUserFetched = !!get(state, ['userSession', 'user', 'id']);
        const companies =
          get(state, ['userSession', 'companies'], []).length > 0;

        const isTranslationInitialized = get(
          state,
          ['userSession', 'i18n', 'translationInitialized'],
          false,
        );

        const isApplicationStarted =
          isPermissionsFetched &&
          isUserFetched &&
          companies &&
          isPreferencesFetched &&
          isTranslationInitialized;

        return isApplicationStarted
          ? observableOf(this.toolsActions.applicationStartupSuccessAction())
          : observableEMPTY;
      }),
    );
  }
}
